import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { proxyImage } from '../utils/imageProxy';

const FeaturedPicks = () => {
  const [wineOfWeek, setWineOfWeek] = useState(null);
  const [staffPick, setStaffPick] = useState(null);

  useEffect(() => {
    fetch('/api/featured/wine-of-week')
      .then(r => r.ok ? r.json() : {})
      .then(data => { if (data.product) setWineOfWeek(data.product); })
      .catch(() => {});

    fetch('/api/featured/staff-pick')
      .then(r => r.ok ? r.json() : {})
      .then(data => { if (data.product) setStaffPick(data.product); })
      .catch(() => {});
  }, []);

  if (!wineOfWeek && !staffPick) return null;

  const getEmailLink = (product) => {
    const msg = encodeURIComponent(`Hi Brunchi! I'm interested in ${product.name} that I saw on your website.`);
    return `mailto:consultas@brunchi.shop?subject=Brunchi.shop%20enquiry&body=${msg}`;
  };

  const getProductImage = (product) => {
    if (product.image_url) {
      return proxyImage(product.image_url, 400);
    }
    if (product.images && product.images.length > 0) {
      return proxyImage(product.images[0], 400);
    }
    return null;
  };

  const getCategoryLink = (product) => {
    const cat = (product.category || '').toLowerCase();
    if (cat.includes('wine')) return '/wines';
    if (cat.includes('beer')) return '/beers';
    if (cat.includes('spirit') || cat.includes('whisky') || cat.includes('gin') || cat.includes('rum') || cat.includes('vodka')) return '/spirits';
    return '/products';
  };

  return (
    <section className="py-12 bg-gradient-to-b from-amber-50 to-white" data-testid="featured-picks">
      <div className="container mx-auto px-4">
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-2">This Week's Picks</h2>
          <p className="text-gray-600">Our team's picks this week</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
          {/* Wine of the Week */}
          {wineOfWeek && (
            <Card className="overflow-hidden hover:shadow-lg transition-shadow group" data-testid="wine-of-week-card">
              <div className="relative">
                <div className="absolute top-3 left-3 z-10">
                  <Badge className="bg-red-600 text-white text-xs font-bold px-3 py-1">Wine of the Week</Badge>
                </div>
                {getProductImage(wineOfWeek) ? (
                  <img 
                    src={getProductImage(wineOfWeek)} 
                    alt={`Wine of the Week: ${wineOfWeek.name} — ${wineOfWeek.category || 'Lanzarote wine'} recommended by Brunchi.shop, Marina Rubicon`}
                    title={`Wine of the Week: ${wineOfWeek.name} | Brunchi.shop`}
                    width="400" height="192"
                    loading="lazy" decoding="async"
                    className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                ) : (
                  <div className="w-full h-48 bg-gradient-to-br from-red-800 to-red-900 flex items-center justify-center">
                    <span className="text-white/50 text-6xl">&#127863;</span>
                  </div>
                )}
              </div>
              <CardContent className="pt-4">
                <h3 className="text-lg font-bold text-gray-900 mb-1">{wineOfWeek.name}</h3>
                <p className="text-sm text-gray-500 mb-3">{wineOfWeek.category}</p>
                {wineOfWeek.description && (
                  <p className="text-sm text-gray-600 mb-3 line-clamp-2">{wineOfWeek.description}</p>
                )}
                <div className="flex items-center justify-between">
                  <Link to={getCategoryLink(wineOfWeek)} className="text-blue-600 hover:text-blue-700 text-sm font-medium">
                    View wines →
                  </Link>
                  <a
                    href={getEmailLink(wineOfWeek)}
                    className="inline-flex items-center gap-1 px-3 py-1.5 bg-green-500 hover:bg-blue-600 text-white text-xs rounded-lg font-medium transition-colors"
                  >
                    Enquire by Email
                  </a>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Staff Pick of the Day */}
          {staffPick && (
            <Card className="overflow-hidden hover:shadow-lg transition-shadow group" data-testid="staff-pick-card">
              <div className="relative">
                <div className="absolute top-3 left-3 z-10">
                  <Badge className="bg-amber-600 text-white text-xs font-bold px-3 py-1">Staff Pick Today</Badge>
                </div>
                {getProductImage(staffPick) ? (
                  <img 
                    src={getProductImage(staffPick)} 
                    alt={`Staff Pick: ${staffPick.name} — ${staffPick.category || 'our pick'} chosen by the Brunchi team, Marina Rubicon`}
                    title={`Staff Pick: ${staffPick.name} | Brunchi.shop`}
                    width="400" height="192"
                    loading="lazy" decoding="async"
                    className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                ) : (
                  <div className="w-full h-48 bg-gradient-to-br from-amber-700 to-amber-800 flex items-center justify-center">
                    <span className="text-white/50 text-6xl">&#11088;</span>
                  </div>
                )}
              </div>
              <CardContent className="pt-4">
                <h3 className="text-lg font-bold text-gray-900 mb-1">{staffPick.name}</h3>
                <p className="text-sm text-gray-500 mb-3">{staffPick.category}</p>
                {staffPick.description && (
                  <p className="text-sm text-gray-600 mb-3 line-clamp-2">{staffPick.description}</p>
                )}
                <div className="flex items-center justify-between">
                  <Link to={getCategoryLink(staffPick)} className="text-blue-600 hover:text-blue-700 text-sm font-medium">
                    View range →
                  </Link>
                  <a
                    href={getEmailLink(staffPick)}
                    className="inline-flex items-center gap-1 px-3 py-1.5 bg-green-500 hover:bg-blue-600 text-white text-xs rounded-lg font-medium transition-colors"
                  >
                    Enquire by Email
                  </a>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </section>
  );
};

export default FeaturedPicks;
